import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, CheckCircle, ShieldAlert, Eye, EyeOff } from 'lucide-react';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setValidating(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/validate-reset-token/${token}`);
        setValid(true);
        setEmail(res.data.email || '');
      } catch {
        setValid(false);
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6 animate-fadeIn">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/edassist-logo.png" alt="EdAssist" className="h-10" />
          </Link>
        </div>

        {success ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4" data-testid="reset-success">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Password updated!
            </h2>
            <p className="text-slate-600 text-sm">
              Your password has been reset successfully. You can now log in with your new password.
            </p>
            <Button className="w-full mt-4" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </div>
        ) : !token || !valid ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4" data-testid="reset-invalid-token">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Invalid or expired link
            </h2>
            <p className="text-slate-600 text-sm">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <div className="pt-2 space-y-3">
              <Link to="/forgot-password">
                <Button className="w-full" data-testid="request-new-link-btn">Request new reset link</Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="w-full text-indigo-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 space-y-6" data-testid="reset-password-form">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Set new password
              </h1>
              {email && (
                <p className="text-slate-600 text-sm">
                  Resetting password for <strong>{email}</strong>
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">New password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm">Confirm new password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirm"
                    type={showPwd ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your password"
                    className="pl-10"
                    required
                    minLength={6}
                    data-testid="reset-confirm-input"
                  />
                </div>
                {confirm && password !== confirm && (
                  <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                )}
              </div>

              <Button type="submit" className="w-full h-11" disabled={loading || password !== confirm} data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>

            <Link to="/login" className="block text-center">
              <Button variant="ghost" className="text-indigo-600 text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
